import { NgClass, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { delay, of, switchMap, take } from 'rxjs';

import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatTooltip } from '@angular/material/tooltip';

import { NO_PRESCRIPTION_GUID } from '@hm/core/models/pharmacies.const';
import { VisitsService } from '@hm/core/services/visits.service';

import { EleksAppointmentBindingFormComponent } from '@hm/ui/forms/eleks-appointment-binding-form/eleks-appointment-binding-form.component';
import { ManualAppointmentFormComponent } from '@hm/ui/forms/manual-appointment-form/manual-appointment-form.component';

import { ModalActionTypes } from '@lib/core/models/modal-action-types.enum';
import { AppointmentServiceCode, PrescriptionTypes } from '@lib/core/models/prescription.interface';
import { AppointmentEventPlaceType, BindAppointmentRequestModel, Visit } from '@lib/core/models/visit.interface';

import { InfoBadgeColors, InfoBadgeComponent } from '@lib/shared-ui/commons/info-badge/info-badge.component';
import { WarningMessageComponent, WarningMessageType } from '@lib/shared-ui/commons/warning-message/warning-message.component';
import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

const ELEKS_ERROR = `Помилка при скасуванні зі сторони Eleks. Потрібно скасувати у партнера.`;
const LILO_ERROR = 'Помилка при скасуванні зі сторони lilo.';

type AppointmentModalFlow = 'binding' | 'manual' | null;

interface ModalControls {
  appointmentType: FormControl<PrescriptionTypes | null>;
  name: FormControl<string | null>;
  appointmentModalFlow: FormControl<AppointmentModalFlow>;
  manualAppointment: FormControl<AppointmentValue | null>;
  eleksBinding: FormControl<EleksBindingValue | null>;
  franchise: FormControl<number | null>;
}

interface EleksBindingValue {
  externalAppointmentId: string;
  doctorId: string;
  clinicAddress: string;
  locationId: string;
  comment: string;
}

interface AppointmentValue {
  prescriptionCodes: AppointmentServiceCode[];
  prescriptionIds: string[];
  appointmentType: PrescriptionTypes | null;
  name: string | null;
  price: number | null;
  doctorName: string | null;
  dateStart: Date | null;
  time: string | null;
  partnerId: string | null;
  partnerName: string | null;
  locationId: string | null;
  location: string | null;
  appointmentEventPlaceType: AppointmentEventPlaceType | null;
  appointmentCompletedFlag: boolean | null;
  franchise: number | null;
}

@Component({
  selector: 'hm-manual-appointment-edit-modal',
  imports: [
    MatDialogActions,
    MatDialogContent,
    LoaderComponent,
    MatInputModule,
    ReactiveFormsModule,
    NgOptimizedImage,
    MatRadioGroup,
    MatRadioButton,
    FormsModule,
    ManualAppointmentFormComponent,
    EleksAppointmentBindingFormComponent,
    TranslatePipe,
    NgClass,
    InfoBadgeComponent,
    MatIcon,
    MatTooltip,
    WarningMessageComponent,
  ],
  templateUrl: './appointment-management-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppointmentManagementModalComponent implements OnInit {
  private readonly data: {
    actionType: ModalActionTypes;
    action?: Visit;
    appointment?: Visit;
  } = inject(MAT_DIALOG_DATA);

  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef);
  private readonly visitsService = inject(VisitsService);
  private readonly translateService = inject(TranslateService);

  protected readonly types = ModalActionTypes;
  protected readonly colors = InfoBadgeColors;
  protected readonly warningTypes = WarningMessageType;
  protected readonly eleksError = ELEKS_ERROR;

  protected readonly type: ModalActionTypes = this.data.actionType;
  protected readonly action: Visit = this.data.action;
  protected readonly appointment: Visit = this.data.appointment;

  protected modalForm: FormGroup<ModalControls>;

  protected readonly loading = signal<boolean>(false);
  protected readonly errorMessage = signal<string>('');

  protected get fc() {
    return this.modalForm.controls;
  }

  ngOnInit() {
    this.initModalForm();
  }

  private initModalForm(): void {
    let appointmentModalFlow: AppointmentModalFlow = null;
    if (this.type === ModalActionTypes.Edit) appointmentModalFlow = this.appointment.metadata.doctor_id === NO_PRESCRIPTION_GUID ? 'manual' : 'binding';

    this.modalForm = new FormGroup<ModalControls>({
      appointmentType: this.fb.control(this.type === ModalActionTypes.Create ? this.action.metadata.type : this.appointment.metadata.type, [Validators.required]),
      name: this.fb.control(this.type === ModalActionTypes.Create ? this.action.metadata.name : this.appointment.metadata.name, [Validators.required]),
      appointmentModalFlow: this.fb.control<AppointmentModalFlow>(appointmentModalFlow, [Validators.required]),
      manualAppointment: this.fb.control<AppointmentValue | null>(null),
      eleksBinding: this.fb.control<EleksBindingValue | null>(null),
      franchise: this.fb.control<number | null>(this.getFranchiseValue()),
    });
  }

  private getFranchiseValue(): number {
    if (this.type === ModalActionTypes.Create && this.action.prescriptions?.length > 0) {
      const prescription = this.action.prescriptions[0];
      return (prescription as { franchise?: number }).franchise ?? 0;
    } else if (this.type === ModalActionTypes.Edit && this.appointment.prescriptions?.length > 0) {
      const prescription = this.appointment.prescriptions[0];
      return (prescription as { franchise?: number }).franchise ?? 0;
    }
    return 0;
  }

  protected getFranchiseLabel(percentage: number): string {
    if (percentage === 0) return 'Клієнт отримає послугу безкоштовно';
    else return `Клієнт повинен сплатити ${percentage}% вартості послуги`;
  }

  protected submit(): void {
    const selectedFlow = this.modalForm.controls.appointmentModalFlow.value;

    if (selectedFlow === 'binding') {
      this.handleEleksBinding();
      return;
    }

    switch (this.type) {
      case ModalActionTypes.Create: {
        this.createAppointment();
        break;
      }
      case ModalActionTypes.Edit: {
        this.updateAppointment();
        break;
      }
      case ModalActionTypes.Delete: {
        this.cancelAppointment();
        break;
      }
    }
  }

  protected isFormValid(): boolean {
    const selectedFlow = this.modalForm.controls.appointmentModalFlow.value;

    if (!selectedFlow) return false;
    if (selectedFlow === 'binding') return this.modalForm.controls.eleksBinding.valid;
    if (selectedFlow === 'manual') return this.modalForm.controls.manualAppointment.valid;

    return false;
  }

  private handleEleksBinding(): void {
    this.markFormGroupTouched(this.modalForm);
    if (!this.isFormValid()) return;

    const eleksBindingFormValue = this.modalForm.controls.eleksBinding.value;
    if (!eleksBindingFormValue) return;

    this.loading.set(true);
    this.errorMessage.set('');

    const bindingData: BindAppointmentRequestModel =
      this.type === ModalActionTypes.Create
        ? {
            externalAppointmentId: eleksBindingFormValue.externalAppointmentId,
            eventPlace: AppointmentEventPlaceType.Hospital,
            actionId: this.action.id,
            doctorId: eleksBindingFormValue.doctorId,
            location: eleksBindingFormValue.clinicAddress,
            locationId: eleksBindingFormValue.locationId,
            comment: eleksBindingFormValue.comment,
            franchise: this.modalForm.controls.franchise.value,
          }
        : {
            externalAppointmentId: eleksBindingFormValue.externalAppointmentId,
            eventPlace: AppointmentEventPlaceType.Hospital,
            doctorId: eleksBindingFormValue.doctorId,
            location: eleksBindingFormValue.clinicAddress,
            locationId: eleksBindingFormValue.locationId,
            comment: eleksBindingFormValue.comment,
            franchise: this.modalForm.controls.franchise.value,
          };

    if (this.type === ModalActionTypes.Create) {
      this.visitsService
        .bindEleksAppointment(this.action.id, bindingData)
        .pipe(delay(1000), take(1))
        .subscribe({
          next: () => {
            this.dialogRef.close(true);
            this.loading.set(false);
          },
          error: error => {
            console.error('Error binding Eleks appointment:', error);
            if (error.error?.Message) {
              this.errorMessage.set(error.error.Message);
            } else {
              this.errorMessage.set(this.translateService.instant('errors.appointments.eleksBinding'));
            }
            this.loading.set(false);
          },
        });
    } else if (this.type === ModalActionTypes.Edit) {
      this.visitsService
        .updateBindEleksAppointment(this.appointment.systemMetadata.appointment_id, bindingData)
        .pipe(delay(1000), take(1))
        .subscribe({
          next: () => {
            this.dialogRef.close(true);
            this.loading.set(false);
          },
          error: error => {
            console.error('Error re-binding Eleks appointment:', error);
            if (error.error?.Message) {
              this.errorMessage.set(error.error.Message);
            } else {
              this.errorMessage.set(this.translateService.instant('errors.appointments.eleksBinding'));
            }
            this.loading.set(false);
          },
        });
    }
  }

  private createAppointment(): void {
    this.markFormGroupTouched(this.modalForm);
    if (this.modalForm.invalid) return;

    this.loading.set(true);
    const formValue: AppointmentValue = this.modalForm.controls.manualAppointment.value;
    const requestBody = {
      ...formValue,
      dateStart: this.appendTimeToDate(formValue.time, formValue.dateStart),
      parentActionId: this.action.id,
      franchise: this.modalForm.controls.franchise.value,
    };
    this.visitsService
      .createManualAppointment(requestBody)
      .pipe(
        switchMap(appointment => {
          if (formValue.appointmentCompletedFlag) {
            return this.visitsService.completeManualAppointment(appointment.id);
          } else {
            return of(null);
          }
        }),
        delay(1000),
        take(1)
      )
      .subscribe({
        next: () => {
          this.dialogRef.close(true);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }

  private updateAppointment(): void {
    this.markFormGroupTouched(this.modalForm);
    if (this.modalForm.invalid) return;

    this.loading.set(true);
    const formValue: AppointmentValue = this.modalForm.controls.manualAppointment.value;
    const requestBody = {
      ...formValue,
      dateStart: this.appendTimeToDate(formValue.time, formValue.dateStart),
      franchise: this.modalForm.controls.franchise.value,
    };
    this.visitsService
      .updateManualAppointment(this.appointment.metadata.appointment_id, requestBody)
      .pipe(
        switchMap(appointment => {
          if (formValue.appointmentCompletedFlag) {
            return this.visitsService.completeManualAppointment(appointment.id);
          } else {
            return of(null);
          }
        }),
        delay(1000),
        take(1)
      )
      .subscribe({
        next: () => {
          this.dialogRef.close(true);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }

  private cancelAppointment(): void {
    this.loading.set(true);
    this.errorMessage.set('');
    this.visitsService
      .cancelManualAppointment(this.appointment.metadata.appointment_id)
      .pipe(delay(1000), take(1))
      .subscribe({
        next: response => {
          if (response.cancellationStatusFailedToSync) {
            this.errorMessage.set(ELEKS_ERROR);
            this.loading.set(false);
          } else {
            this.dialogRef.close(true);
          }
        },
        error: () => {
          this.errorMessage.set(LILO_ERROR);
          this.loading.set(false);
        },
      });
  }

  protected closeDialog(): void {
    const updateExpected = this.errorMessage() === ELEKS_ERROR;
    this.dialogRef.close(updateExpected);
  }

  private appendTimeToDate(time: string, date: Date): Date {
    const hours: number = +time.split(':')[0];
    const minutes: number = +time.split(':')[1];
    const dateTimeStamp = new Date(date).setHours(hours, minutes);

    return new Date(dateTimeStamp);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control instanceof FormGroup) this.markFormGroupTouched(control);
    });
  }
}
